import { Division, Flex, Icon, Icons } from "@ir/ui";
import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import "./AccordianCustom.scss";

export interface AccordianCustomItem {
  key: string;
  title: ReactNode;
  content: ReactNode;
}

export interface AccordianCustomProps {
  items: AccordianCustomItem[];
  /**
   * Controls how `defaultExpandedKeys` is applied on load (and when items/defaults sync).
   * When true, every valid key in `defaultExpandedKeys` opens initially.
   * When false, only a single key opens initially; multiple keys in defaults means start collapsed.
   * User clicks always toggle sections independently — any number can stay open until closed or reload.
   */
  allowsMultipleExpanded?: boolean;
  /** Keys that start expanded (interpretation depends on `allowsMultipleExpanded`). */
  defaultExpandedKeys?: string[];
  className?: string;
  style?: CSSProperties;
}

function compareAlphabetically(a: string, b: string): number {
  return a.localeCompare(b);
}

function getInitialExpandedKeys(
  items: AccordianCustomItem[],
  allowsMultipleExpanded: boolean,
  defaultExpandedKeys: string[],
): string[] {
  if (!defaultExpandedKeys.length) return [];
  const validKeys = new Set(items.map((item) => item.key));
  const next = defaultExpandedKeys.filter((key) => validKeys.has(key));
  if (!next.length) return [];
  if (allowsMultipleExpanded) return next;
  // Single-expand: only a single explicit key opens initially. If multiple keys are passed
  // (e.g. copied from multi-expand usage), treat as ambiguous and start with all collapsed.
  if (next.length === 1) return next;
  return [];
}

export const AccordianCustom = ({
  items,
  allowsMultipleExpanded = false,
  defaultExpandedKeys = [],
  className,
  style,
}: Readonly<AccordianCustomProps>) => {
  const initialExpandedKeys = useMemo(
    () =>
      getInitialExpandedKeys(
        items,
        allowsMultipleExpanded,
        defaultExpandedKeys,
      ),
    [items, allowsMultipleExpanded, defaultExpandedKeys],
  );
  const [expandedKeys, setExpandedKeys] =
    useState<string[]>(initialExpandedKeys);

  const itemKeysSig = items
    .map((item) => item.key)
    .sort(compareAlphabetically)
    .join("|");
  const defaultKeysSig = defaultExpandedKeys
    .slice()
    .sort(compareAlphabetically)
    .join("|");

  useEffect(() => {
    setExpandedKeys(
      getInitialExpandedKeys(
        items,
        allowsMultipleExpanded,
        defaultExpandedKeys,
      ),
    );
    // Only re-apply defaults when item keys or default keys (content) change — not when parent
    // passes new array references each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- items/defaults keyed by *Sig above
  }, [itemKeysSig, defaultKeysSig, allowsMultipleExpanded]);

  const expandedKeySet = useMemo(() => new Set(expandedKeys), [expandedKeys]);

  const toggleItem = (key: string) => {
    setExpandedKeys((prev) => {
      const isExpanded = prev.includes(key);
      if (isExpanded) return prev.filter((itemKey) => itemKey !== key);
      return [...prev, key];
    });
  };

  return (
    <Flex
      direction="column"
      className={`accordian-custom-list ${className ?? ""}`.trim()}
      style={style}
    >
      {items.map((item) => {
        const expanded = expandedKeySet.has(item.key);
        return (
          <Flex
            key={item.key}
            direction="column"
            align="stretch"
            className={`accordian-custom ${expanded ? "is-expanded" : ""}`.trim()}
          >
            <Flex
              align="center"
              justifyContent="space-between"
              className="accordian-custom-header"
            >
              <Flex className="accordian-custom-title">{item.title}</Flex>
              <button
                type="button"
                onClick={() => toggleItem(item.key)}
                aria-label={expanded ? "Collapse section" : "Expand section"}
                aria-expanded={expanded}
                className="accordian-custom-toggle"
              >
                <Icon color="grey" name={Icons.ChevronRight} />
              </button>
            </Flex>
            <Division className="accordian-custom-content">
              <Division className="accordian-custom-content-inner">
                {item.content}
              </Division>
            </Division>
          </Flex>
        );
      })}
    </Flex>
  );
};
